import * as React from 'react'
import {
  Check,
  Download,
  Loader2,
  Maximize2,
  Minimize2,
  Pencil,
  RefreshCcw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import type { BracketMakerCopy, BracketTheme } from './types'

type ZoomControlsProps = {
  className?: string
  copy: BracketMakerCopy
  onZoomIn: () => void
  onZoomOut: () => void
  theme: BracketTheme
  zoom: number
}

function ZoomControls({
  className,
  copy,
  onZoomIn,
  onZoomOut,
  theme,
  zoom,
}: ZoomControlsProps) {
  return (
    <div className={cn('items-center gap-1 text-xs', className)}>
      <span className={theme.zoomBadge}>{Math.round(zoom * 100)}%</span>
      <Button
        variant="ghost"
        size="icon"
        className={theme.actionIconButton}
        onClick={onZoomOut}
        title={copy.zoomOut}
      >
        <ZoomOut className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={theme.actionIconButton}
        onClick={onZoomIn}
        title={copy.zoomIn}
      >
        <ZoomIn className="h-4 w-4" />
      </Button>
    </div>
  )
}

type CanvasActionButtonsProps = {
  className?: string
  copy: BracketMakerCopy
  isExporting: boolean
  isUpdating: boolean
  isFullscreen: boolean
  onExport: () => void
  onResetWinners: () => void
  onToggleFullscreen: () => void
  theme: BracketTheme
}

function CanvasActionButtons({
  className,
  copy,
  isExporting,
  isUpdating,
  isFullscreen,
  onExport,
  onResetWinners,
  onToggleFullscreen,
  theme,
}: CanvasActionButtonsProps) {
  return (
    <div className={cn(theme.canvasActionBar, className)}>
      <Button
        variant="ghost"
        size="icon"
        className={theme.actionIconButton}
        onClick={onResetWinners}
        title={copy.resetWinners}
      >
        <RefreshCcw className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 gap-2"
        onClick={onExport}
        aria-label={isExporting ? copy.exporting : copy.exportPng}
        title={
          isExporting
            ? copy.exporting
            : isUpdating
              ? copy.updating
              : copy.exportPng
        }
        disabled={isExporting || isUpdating}
        aria-busy={isExporting}
      >
        {isExporting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={theme.actionIconButton}
        onClick={onToggleFullscreen}
        title={isFullscreen ? copy.exitFullscreen : copy.fullscreen}
      >
        {isFullscreen ? (
          <Minimize2 className="h-4 w-4" />
        ) : (
          <Maximize2 className="h-4 w-4" />
        )}
      </Button>
    </div>
  )
}

type BracketToolbarProps = {
  bracketTitle: string
  copy: BracketMakerCopy
  defaultBracketTitle: string
  isEditingTitle: boolean
  isExporting: boolean
  isUpdating: boolean
  isFullscreen: boolean
  onCancelTitleEditing: () => void
  onExport: () => void
  onFinishTitleEditing: () => void
  onResetWinners: () => void
  onStartTitleEditing: () => void
  onTitleChange: (value: string) => void
  onToggleFullscreen: () => void
  onZoomIn: () => void
  onZoomOut: () => void
  theme: BracketTheme
  titleInputRef: React.RefObject<HTMLInputElement | null>
  zoom: number
}

export function BracketToolbar({
  bracketTitle,
  copy,
  defaultBracketTitle,
  isEditingTitle,
  isExporting,
  isUpdating,
  isFullscreen,
  onCancelTitleEditing,
  onExport,
  onFinishTitleEditing,
  onResetWinners,
  onStartTitleEditing,
  onTitleChange,
  onToggleFullscreen,
  onZoomIn,
  onZoomOut,
  theme,
  titleInputRef,
  zoom,
}: BracketToolbarProps) {
  const titleControl = (
    <div className="flex w-full min-w-0 items-center justify-center gap-2 px-1 md:order-2 md:w-auto md:px-4">
      {isEditingTitle ? (
        <input
          ref={titleInputRef}
          value={bracketTitle}
          onChange={(event) => onTitleChange(event.target.value)}
          onBlur={onFinishTitleEditing}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              onFinishTitleEditing()
            }
            if (event.key === 'Escape') {
              event.preventDefault()
              onCancelTitleEditing()
            }
          }}
          className={theme.canvasTitleInput}
          placeholder={defaultBracketTitle}
        />
      ) : (
        <button
          type="button"
          onClick={onStartTitleEditing}
          className={theme.canvasTitleButton}
          title={copy.editTitle}
        >
          <span className="truncate">{bracketTitle}</span>
        </button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={theme.actionIconButton}
        onClick={isEditingTitle ? onFinishTitleEditing : onStartTitleEditing}
        title={isEditingTitle ? copy.saveTitle : copy.editTitle}
      >
        {isEditingTitle ? (
          <Check className="h-4 w-4" />
        ) : (
          <Pencil className="h-4 w-4" />
        )}
      </Button>
    </div>
  )

  return (
    <div className={theme.canvasToolbar}>
      {titleControl}

      <div className="flex w-full items-center justify-between gap-2 px-1 md:hidden">
        <ZoomControls
          className="flex flex-nowrap"
          copy={copy}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          theme={theme}
          zoom={zoom}
        />
        <CanvasActionButtons
          className="justify-end"
          copy={copy}
          isExporting={isExporting}
          isUpdating={isUpdating}
          isFullscreen={isFullscreen}
          onExport={onExport}
          onResetWinners={onResetWinners}
          onToggleFullscreen={onToggleFullscreen}
          theme={theme}
        />
      </div>

      <ZoomControls
        className="hidden w-full flex-wrap md:order-1 md:flex md:w-auto md:flex-nowrap"
        copy={copy}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        theme={theme}
        zoom={zoom}
      />

      <CanvasActionButtons
        className="hidden w-full md:order-3 md:flex md:w-auto"
        copy={copy}
        isExporting={isExporting}
        isUpdating={isUpdating}
        isFullscreen={isFullscreen}
        onExport={onExport}
        onResetWinners={onResetWinners}
        onToggleFullscreen={onToggleFullscreen}
        theme={theme}
      />
    </div>
  )
}
